import React, { useEffect, useState, useContext, useCallback } from "react";
import { adminApi } from "../apiAdmin";
import { pagedItems, PAGE_SIZE } from "../utils/adminPaging";
import { isNotFound, withQuietRetry } from "../utils/adminRetry";
import { DISPUTE_SUMMARY_URL, disputeSummaryFrom } from "../components/admin-stats/api";
import { AdminAuthContext } from "../context/AdminAuthContext";
import "./AdminDashboardStyles.css";
import "./AdminSidebar.css";
import { useNavigate } from "react-router-dom";
import AdminOrdersTab from "../components/AdminOrdersTab";
import AdminOverviewTab from "../components/AdminOverviewTab";
import AdminEstablishmentsTab from "../components/AdminEstablishmentsTab";
import AdminAllPayoutsTab from "../components/AdminAllPayoutsTab";
import AdminTrendingTab from "../components/AdminTrendingTab";
import AdminDisputesTab from "../components/AdminDisputesTab";
import AdminPaymentIssuesTab from "../components/AdminPaymentIssuesTab";
import AdminFeedbackTab from "../components/AdminFeedbackTab";
import AdminHealthTab from "../components/AdminHealthTab";
import AdminGrowthTab from "../components/AdminGrowthTab";
import AdminCountriesTab from "../components/AdminCountriesTab";
import AdminGuardrailsTab from "../components/AdminGuardrailsTab";
import AdminCostsTab from "../components/AdminCostsTab";
import AdminUsersTab, { talentSearchUrl } from "../components/AdminUsersTab";
import AdminFansTab from "../components/AdminFansTab";
import AdminRevenueTab from "../components/AdminRevenueTab";
import AdminAdminsTab from "../components/AdminAdminsTab";
import AdminApprovalsTab from "../components/AdminApprovalsTab";
import AdminAmbassadorsTab from "../components/AdminAmbassadorsTab";
import { TimeRangeKey } from "../utils/dateRange";
import { User, TalentFilter, TALENT_FILTERS, EarnStageCounts } from "../types/admin";
import { AdminTab as Tab, NAV_GROUPS, TABS } from "../components/adminNav";

// Admin dashboard SHELL: the sidebar / phone tab strip, the header bar, the
// shared data several tabs read (talent, admins, badge counts) and the tab
// switch. Each tab's own UI lives in components/Admin*Tab.tsx; the Talent /
// Revenue / Admins tabs and their modals moved out in the Phase C split (see
// src/types/admin.ts for the shared record shapes). The order history is
// never downloaded: the Dashboard, Requests, Revenue and Trending tabs each
// ask the server for exactly what they show (server lib/admin/stats.js).

export default function AdminDashboard() {
  const { admin, logout } = useContext(AdminAuthContext);
  const navigate = useNavigate();
  // Deep-linkable tab: /admin/dashboard?tab=allPayouts opens straight into a
  // tab (the "Admin" home-screen icon points at All Payouts). Falls back to
  // Dashboard for a missing/unknown value.
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("tab");
      if (q && TABS.some((t) => t.id === q)) return q as Tab;
    } catch { /* no-op */ }
    return "overview";
  });

  // Shared time window for the money-over-time tabs (Dashboard, Revenue,
  // All Payouts). Owned here so the selection persists as the admin switches
  // between them. Defaults to "All Time". Read-only — it only narrows which
  // records are summed; nothing stored ever changes, and each currency stays
  // split on its own (see formatMoneyByCurrency).
  const [range, setRange] = useState<TimeRangeKey>("all");

  const [users, setUsers] = useState<User[]>([]);
  // True talent count from the paged /users response — the badge and the
  // "Load more" control both need the total, not just the loaded page.
  const [usersTotal, setUsersTotal] = useState(0);
  // The "Ready to earn" chip counts, over every talent (not just loaded pages).
  const [talentStages, setTalentStages] = useState<EarnStageCounts | null>(null);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [admins, setAdmins] = useState<User[]>([]);
  // Bumped by the Requests header's ↻ Refresh so that tab reads its view again.
  const [requestsRefresh, setRequestsRefresh] = useState(0);

  const [userQuery, setUserQuery] = useState("");
  const [talentFilter, setTalentFilter] = useState<TalentFilter>(() => {
    try {
      const f = new URLSearchParams(window.location.search).get("filter");
      return TALENT_FILTERS.find((x) => x === f) || "all";
    } catch {
      return "all";
    }
  });

  // Notification badges
  const [disputeCount, setDisputeCount] = useState(0);
  const [payoutCount, setPayoutCount] = useState(0);
  const [lastSeenPayoutCount, setLastSeenPayoutCount] = useState<number | null>(null);
  const [talentCount, setTalentCount] = useState(0);
  // Talent waiting for Approve / Decline. A real to-do count, so unlike the
  // "new since last visit" badges it stays until the list is empty.
  const [approvalCount, setApprovalCount] = useState(0);
  const [lastSeenTalentCount, setLastSeenTalentCount] = useState<number | null>(null);

  // "+ Add Talent" lives in the header bar below, so the open flag is owned
  // here; the form itself is AdminAddTalentModal, rendered by the Talent tab.
  const [showAddTalent, setShowAddTalent] = useState(false);

  // A failed read says so ("Couldn't load" + Retry on the tab that shows it)
  // instead of leaving an empty list; rows already loaded stay. Every read
  // gets one quiet second try first (utils/adminRetry). The badges stay
  // silent: a badge that didn't load just isn't drawn.
  const [usersFailed, setUsersFailed] = useState(false);
  const [adminsFailed, setAdminsFailed] = useState(false);
  const [loadMoreUsersFailed, setLoadMoreUsersFailed] = useState(false);
  // Bumped by every fetchAll after the first, so ↻ Refresh also re-asks an
  // open talent search.
  const [refreshTick, setRefreshTick] = useState(0);
  const fetchedOnce = React.useRef(false);

  const fetchAll = useCallback(() => {
    if (fetchedOnce.current) setRefreshTick((n) => n + 1);
    fetchedOnce.current = true;
    setUsersFailed(false);
    setAdminsFailed(false);
    // Paged fetches (server opt-in pagination). First page only — "Load more" appends the
    // rest; badges use the server-side total so they stay exact at any scale.
    withQuietRetry(() => adminApi.get(`/users?limit=${PAGE_SIZE}`)).then((r) => {
      const { items, total } = pagedItems<User>(r.data);
      setUsers(items);
      setUsersTotal(total);
      setTalentStages(r.data?.stages || null);
      const stored = localStorage.getItem("lastSeenTalentCount");
      const lastSeen = stored !== null ? parseInt(stored) : total;
      if (lastSeenTalentCount === null) setLastSeenTalentCount(lastSeen);
      setTalentCount(Math.max(0, total - lastSeen));
    }, () => setUsersFailed(true));
    // No order history here: the four order tabs add orders up on the
    // server, over the whole history, and fetch only what they show.
    withQuietRetry(() => adminApi.get("/admins")).then((r) => setAdmins(r.data), () => setAdminsFailed(true));
    // Badge only needs the total — limit=1 keeps the payload tiny.
    withQuietRetry(() => adminApi.get("/payouts?limit=1")).then((r) => {
      const { total } = pagedItems(r.data);
      const stored = localStorage.getItem("lastSeenPayoutCount");
      const lastSeen = stored !== null ? parseInt(stored) : total;
      if (lastSeenPayoutCount === null) setLastSeenPayoutCount(lastSeen);
      const newCount = Math.max(0, total - lastSeen);
      setPayoutCount(newCount);
    }).catch(() => {});
    withQuietRetry(() => adminApi.get("/approvals")).then((r) => setApprovalCount((r.data?.items || []).length)).catch(() => {});
    // Open disputes, counted on the server over every dispute. A server from
    // before /disputes/summary (404) gets the old count: open disputes are
    // recent by nature, so the newest 500 cover the badge.
    withQuietRetry(() => adminApi.get(DISPUTE_SUMMARY_URL))
      .then((r) => setDisputeCount(disputeSummaryFrom(r.data).needsResponse))
      .catch((err) => {
        if (!isNotFound(err)) return;
        return withQuietRetry(() => adminApi.get("/disputes?limit=500")).then((r) => {
          const { items } = pagedItems<{ status: string }>(r.data);
          const open = items.filter((d) =>
            d.status === "needs_response" || d.status === "warning_needs_response"
          );
          setDisputeCount(open.length);
        });
      })
      .catch(() => {});
    // Stable fetcher on purpose — it is polled and shared by several effects.
    // lastSeen* are read once, on first load, to seed the badges.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Talent search and chips: with text in the box (typing settles 300 ms) or
  // a chip other than All, the server searches EVERY talent and its rows are
  // shown. The tab still filters locally on top, and the rows already loaded
  // stay in the mix, so a server that ignores q / stage behaves exactly as
  // before. The chip counts stay the first page's `stages` (every talent).
  // Asked only while the Talent tab is open.
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(userQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [userQuery]);
  const searchUrl = tab === "users" ? talentSearchUrl(searchQuery, talentFilter) : null;
  const [talentSearch, setTalentSearch] = useState<{ url: string; items: User[] } | null>(null);
  const [talentSearchFailed, setTalentSearchFailed] = useState(false);
  const searchCall = React.useRef(0);
  const runTalentSearch = useCallback(() => {
    const mine = ++searchCall.current;
    setTalentSearchFailed(false);
    if (!searchUrl) return;
    withQuietRetry(() => adminApi.get(searchUrl)).then(
      (r) => {
        if (mine !== searchCall.current) return;
        setTalentSearch({ url: searchUrl, items: pagedItems<User>(r.data).items });
      },
      () => mine === searchCall.current && setTalentSearchFailed(true)
    );
  }, [searchUrl]);
  useEffect(runTalentSearch, [runTalentSearch, refreshTick]);
  const search = searchUrl && talentSearch?.url === searchUrl ? talentSearch : null;
  const shownUsers = React.useMemo(() => {
    if (!search) return users;
    const fromServer = new Set(search.items.map((u) => u._id));
    return [...search.items, ...users.filter((u) => !fromServer.has(u._id))];
  }, [search, users]);
  // A row the tab changes (freeze, unfreeze, delete) changes in both lists.
  const updateUsers: React.Dispatch<React.SetStateAction<User[]>> = useCallback((next) => {
    setUsers(next);
    if (typeof next === "function") setTalentSearch((s) => (s ? { ...s, items: next(s.items) } : s));
  }, []);

  // "Load more" pages the talent list itself, search or not, as it always
  // did; a search's rows come on top of it.
  const loadMoreUsers = () => {
    setLoadingMoreUsers(true);
    setLoadMoreUsersFailed(false);
    withQuietRetry(() => adminApi.get(`/users?limit=${PAGE_SIZE}&skip=${users.length}`))
      .then((r) => {
        const { items, total } = pagedItems<User>(r.data);
        setUsers((prev) => [...prev, ...items]);
        setUsersTotal(total);
      })
      .catch(() => setLoadMoreUsersFailed(true))
      .finally(() => setLoadingMoreUsers(false));
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // Ends every session this admin has, on every device, this one included.
  const handleLogoutEverywhere = async () => {
    if (!window.confirm("Sign out of the admin panel on every device, including this one?")) return;
    try {
      await adminApi.post("/sessions/revoke");
    } catch {
      window.alert("Could not sign out everywhere. Check your connection and try again.");
      return;
    }
    handleLogout();
  };

  const tabTitle = TABS.find((t) => t.id === tab)?.label ?? "Dashboard";

  // The red count on a tab, the same in the sidebar and the phone strip.
  const badgeCount: Partial<Record<Tab, number>> = {
    disputes: disputeCount,
    allPayouts: payoutCount,
    users: talentCount,
    approvals: approvalCount,
  };
  const badge = (id: Tab) => {
    const n = badgeCount[id] || 0;
    return n > 0 ? <span className="dispute-badge-dot">{n}</span> : null;
  };

  // One tab-switch path shared by the desktop sidebar and the phone strip:
  // swaps the tab, mirrors it into the URL (so refresh / home-screen launches
  // land on the same tab), and clears the per-tab "new items" badges.
  const selectTab = (id: Tab) => {
    setTab(id);
    try {
      window.history.replaceState(null, "", `${window.location.pathname}?tab=${id}`);
    } catch { /* no-op */ }
    if (id === "disputes") setDisputeCount(0);
    if (id === "allPayouts") {
      setPayoutCount(0);
      adminApi.get("/payouts?limit=1").then((r) => {
        const { total } = pagedItems(r.data);
        localStorage.setItem("lastSeenPayoutCount", String(total));
        setLastSeenPayoutCount(total);
      }).catch(() => {});
    }
    if (id === "users") {
      setTalentCount(0);
      localStorage.setItem("lastSeenTalentCount", String(usersTotal));
      setLastSeenTalentCount(usersTotal);
    }
  };

  // Keep the active chip visible in the phone strip (no-op on desktop, where
  // the strip is display:none).
  const activeMtabRef = React.useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeMtabRef.current?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [tab]);

  // Phone menu: the strip's "Menu" button opens the grouped list full screen
  // with big rows, so any tab is one tap away instead of a long swipe.
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(false);
  useEffect(() => {
    if (!phoneMenuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPhoneMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phoneMenuOpen]);

  // The grouped tab list: the desktop sidebar and the phone menu draw the same one.
  const groupedMenu = (onPick: (id: Tab) => void) => (
    <nav className="adm-menu">
      {NAV_GROUPS.map((g) => (
        <React.Fragment key={g.title ?? "top"}>
          {g.title && <div className="adm-menu-group">{g.title}</div>}
          {g.tabs.map((t) => (
            <button
              key={t.id}
              className={`adm-menu-btn ${tab === t.id ? "active" : ""}`}
              onClick={() => onPick(t.id)}
            >
              <span className="menu-icon" style={{ color: t.color }}>{t.icon}</span>
              {t.label}
              {badge(t.id)}
            </button>
          ))}
        </React.Fragment>
      ))}
    </nav>
  );

  return (
    <div id="adm-root-light">
      {/* ── Sidebar ── */}
      <aside id="adm-sidebar-light">
        <div className="adm-logo-light">
          <img src="/request-wordmark.png" alt="request" />
          <span>Admin</span>
        </div>
        {groupedMenu(selectTab)}
        <div className="adm-spacer" />
        <div className="adm-sidebar-footer">
          <div className="adm-admin-info">
            <div className="adm-admin-dot" />
            <span className="adm-admin-name">{admin?.name}</span>
          </div>
          <button onClick={handleLogout} className="adm-logout-btn-light">
            Sign out
          </button>
          <button onClick={handleLogoutEverywhere} className="adm-logout-everywhere-light">
            Sign out everywhere
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main id="adm-main-light">
        {/* Phone-only tab strip — replaces the sidebar ≤768px (CSS-hidden on
            desktop). Menu (stays at the left edge while you swipe), then the
            same tabs and badges, plus Sign out at the end. */}
        <nav id="adm-mobile-tabs">
          <button
            className="adm-mtab adm-mtab-menu"
            onClick={() => setPhoneMenuOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={phoneMenuOpen}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
            Menu
          </button>
          {TABS.map((t) => (
            <button
              key={t.id}
              ref={tab === t.id ? activeMtabRef : undefined}
              className={`adm-mtab ${tab === t.id ? "active" : ""}`}
              onClick={() => selectTab(t.id)}
            >
              <span className="menu-icon" style={{ color: t.color }}>{t.icon}</span>
              {t.label}
              {badge(t.id)}
            </button>
          ))}
          <button className="adm-mtab adm-mtab-signout" onClick={handleLogout}>
            Sign out
          </button>
        </nav>
        {phoneMenuOpen && (
          <div className="adm-msheet" role="dialog" aria-modal="true" aria-label="Admin menu">
            <div className="adm-msheet-head">
              <img src="/request-wordmark.png" alt="request" />
              <span>Admin</span>
              <button className="adm-msheet-close" onClick={() => setPhoneMenuOpen(false)} aria-label="Close menu">
                ×
              </button>
            </div>
            <div className="adm-msheet-body">
              {groupedMenu((id) => {
                selectTab(id);
                setPhoneMenuOpen(false);
              })}
            </div>
            <div className="adm-msheet-foot">
              <span className="adm-admin-name">{admin?.name}</span>
              <button onClick={handleLogout} className="adm-logout-btn-light">
                Sign out
              </button>
              <button onClick={handleLogoutEverywhere} className="adm-logout-everywhere-light">
                Sign out everywhere
              </button>
            </div>
          </div>
        )}
        <header id="adm-header">
          <div>
            <h1 className="adm-heading">{tabTitle}</h1>
          </div>
          {tab === "users" && (
            <div className="adm-topbar">
              <input
                className="adm-search"
                placeholder="Search Talent..."
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
              />
              <button className="adm-btn-green" onClick={() => setShowAddTalent(true)}>
                + Add Talent
              </button>
              <button className="adm-btn-light" onClick={fetchAll}>
                ↻ Refresh
              </button>
            </div>
          )}
          {tab === "orders" && (
            <button
              className="adm-btn-light"
              onClick={() => {
                fetchAll();
                setRequestsRefresh((n) => n + 1);
              }}
            >
              ↻ Refresh
            </button>
          )}
        </header>

        <div className="adm-content-area">
          {/* ── Overview ── */}
          {tab === "overview" && (
            <AdminOverviewTab
              talentTotal={usersFailed && users.length === 0 ? null : usersTotal}
              adminsCount={adminsFailed && admins.length === 0 ? null : admins.length}
              range={range}
              onRangeChange={setRange}
              setTab={setTab}
            />
          )}

          {/* ── Orders ── (owns its own data: the server filters and pages) */}
          {tab === "orders" && <AdminOrdersTab users={users} refreshKey={requestsRefresh} />}

          {/* ── Users/DJs ── */}
          {tab === "users" && (
            <AdminUsersTab
              users={shownUsers}
              setUsers={updateUsers}
              usersTotal={usersTotal}
              talentStages={talentStages}
              loadingMoreUsers={loadingMoreUsers}
              loadMoreUsers={loadMoreUsers}
              userQuery={userQuery}
              talentFilter={talentFilter}
              setTalentFilter={setTalentFilter}
              showAddTalent={showAddTalent}
              setShowAddTalent={setShowAddTalent}
              fetchAll={fetchAll}
              usersFailed={usersFailed}
              loadMoreFailed={loadMoreUsersFailed}
              searchFailed={!!searchUrl && talentSearchFailed}
              retrySearch={runTalentSearch}
              loadedCount={users.length}
            />
          )}

          {/* ── Fans ── (owns its own data, like Disputes) */}
          {tab === "fans" && <AdminFansTab />}

          {/* ── Revenue ── (owns its own data) */}
          {tab === "revenue" && <AdminRevenueTab range={range} setRange={setRange} />}

          {/* ── All Payouts ── */}
          {tab === "allPayouts" && <AdminAllPayoutsTab range={range} onRangeChange={setRange} />}

          {/* ── Trending ── (owns its own data) */}
          {tab === "trending" && <AdminTrendingTab />}

          {/* ── Disputes ── */}
          {tab === "disputes" && <AdminDisputesTab />}
          {tab === "paymentIssues" && <AdminPaymentIssuesTab />}
          {tab === "feedback" && <AdminFeedbackTab />}
          {tab === "growth" && (
            <AdminGrowthTab
              onShowNeedsBank={() => {
                setTalentFilter("needsBank");
                selectTab("users");
              }}
            />
          )}
          {tab === "health" && <AdminHealthTab />}
          {tab === "countries" && <AdminCountriesTab />}
          {tab === "guardrails" && <AdminGuardrailsTab />}
          {tab === "costs" && <AdminCostsTab />}
          {tab === "ambassadors" && <AdminAmbassadorsTab />}

          {/* ── Establishments ── */}
          {tab === "establishments" && <AdminEstablishmentsTab allDjs={users} />}

          {tab === "approvals" && <AdminApprovalsTab onCount={setApprovalCount} />}

          {/* ── Admins ── */}
          {tab === "admins" && <AdminAdminsTab admins={admins} setAdmins={setAdmins} fetchAll={fetchAll} adminsFailed={adminsFailed} />}
        </div>
      </main>
    </div>
  );
}
